import React from 'react';
import useTranslate from '../../Hooks/useTranslate';
import {
  Select,
  MenuItem,
  CircularProgress,
  InputLabel,
  FormControl,
} from '@mui/material';
import MultipleChipSelect from '../Inputs/MultipleChipSelect';
import { useLookupOptions } from './useLookupOptions';
import { BlockedField } from './binding/PointedFields';

/**
 * Select for schema fields with a closed option list, declared inline as
 * `options` or fetched from the DataSourceRegistry key in `optionsUrl`.
 * `multiple` renders the chip variant and keeps the value as an array.
 *
 * A field waiting on a sibling it depends on is disabled until that sibling is
 * filled in, and clicking it points at the ones that are missing.
 */
export const DynamicSelect = ({ field, value, onChange, basePath = null }) => {
  const translate = useTranslate();
  const [options, loading, waitingOn] = useLookupOptions(field, basePath, {
    value,
    onChange,
  });
  const blocked = waitingOn.length > 0;

  if (field.multiple)
    return (
      <BlockedField waitingOn={waitingOn}>
        <MultipleChipSelect
          label={translate(field.label)}
          values={Array.isArray(value) ? value : []}
          onChange={onChange}
          options={options}
          disabled={loading || blocked}
          size="small"
        />
      </BlockedField>
    );

  return (
    <BlockedField waitingOn={waitingOn}>
      <FormControl size="small" margin="normal" fullWidth>
        <InputLabel>{translate(field.label)}</InputLabel>
        <Select
          value={value || ''}
          label={translate(field.label)}
          onChange={(e) => onChange(e.target.value)}
          disabled={loading || blocked}
        >
          {loading ? (
            <MenuItem disabled value="">
              <CircularProgress size={20} sx={{ mr: 2 }} />{' '}
              {translate('LOADING')}
            </MenuItem>
          ) : options.length === 0 ? (
            <MenuItem disabled value="">
              {translate('NO_OPTIONS_FOUND')}
            </MenuItem>
          ) : (
            options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {translate(opt.label)}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </BlockedField>
  );
};
